
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, ChevronLeft, ChevronRight, Layers } from 'lucide-react';
import { CreateCardButton } from '@/components/kanban/CreateCardButton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { Tables } from '@/integrations/supabase/types';

type Column = Tables<'columns'>;

interface MobileBottomNavigationProps {
  columns: Column[];
  currentColumnIndex: number;
  onColumnChange: (index: number) => void;
  currentColumn: Column;
}

export const MobileBottomNavigation = ({ 
  columns, 
  currentColumnIndex, 
  onColumnChange,
  currentColumn 
}: MobileBottomNavigationProps) => {
  const [showColumnPicker, setShowColumnPicker] = useState(false);

  const canGoPrevious = currentColumnIndex > 0;
  const canGoNext = currentColumnIndex < columns.length - 1;

  return (
    <div className="flex-shrink-0 bg-background border-t border-border px-4 py-2 safe-area-pb">
      <div className="flex items-center justify-between gap-2">
        {/* Previous Column Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onColumnChange(currentColumnIndex - 1)}
          disabled={!canGoPrevious}
          className="flex items-center gap-1"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="text-xs hidden xs:inline">Previous</span>
        </Button>

        {/* Column Picker */}
        <Dialog open={showColumnPicker} onOpenChange={setShowColumnPicker}>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-2 flex-1 max-w-32"
            >
              <Layers className="h-4 w-4" />
              <span className="text-xs truncate">
                {currentColumnIndex + 1}/{columns.length}
              </span>
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Choose Column</DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-80">
              <div className="space-y-2 p-4">
                {columns.map((column, index) => (
                  <Button
                    key={column.id}
                    variant={index === currentColumnIndex ? "default" : "ghost"}
                    className="w-full justify-start h-auto p-3"
                    onClick={() => {
                      onColumnChange(index);
                      setShowColumnPicker(false);
                    }}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{column.title}</span>
                        {column.is_completed_column && (
                          <Badge variant="secondary" className="text-xs">
                            Complete
                          </Badge>
                        )}
                      </div>
                      {index === currentColumnIndex && (
                        <Badge variant="default" className="text-xs">
                          Current
                        </Badge>
                      )}
                    </div>
                  </Button>
                ))}
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>

        {/* Quick Add Card */}
        <CreateCardButton columnId={currentColumn?.id || ''} />

        {/* Next Column Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onColumnChange(currentColumnIndex + 1)}
          disabled={!canGoNext}
          className="flex items-center gap-1"
        >
          <span className="text-xs hidden xs:inline">Next</span>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
